import { createContext, useMemo, useContext } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { gql, useQuery } from '@zenbusiness/application-commons-graphql';
import { useError, ZBError } from '@zenbusiness/application-commons-logging';

const OrderTrackerStatusContext = createContext(null);

export const GET_ORDER_TASK_STATUS = gql`
  query orderTasksByBusinessEntity($businessEntityUuid: UUID!) {
    orderTasksByBusinessEntity(businessEntityUuid: $businessEntityUuid) {
      type
      status
      jurisdictionCode
      data {
        ... on OrderTaskDataLlcFormation {
          estimatedFormationDate
        }
        ... on OrderTaskDataDefault {
          service
        }
        ... on OrderTaskDataPlan {
          plan
          level
          jurisdiction
        }
      }
    }
  }
`;

/**
 * Provides order tracker status from a single GraphQL subscription so multiple
 * consumers (Home redirect, OrderTracker) don't
 * each trigger duplicate orderTasksByBusinessEntity queries.
 * Must be rendered inside BusinessEntityProvider.
 * @param {object} props
 * @param {import('react').ReactNode} props.children
 * @returns {import('react').ReactElement}
 */
function OrderTrackerStatusProvider({ children }) {
  const { businessEntityUuid } = useBusinessEntity();
  const { loading, data, error } = useQuery(GET_ORDER_TASK_STATUS, {
    noThrow: true,
    skip: !businessEntityUuid,
    variables: {
      businessEntityUuid,
    },
  });
  useError(error);

  const value = useMemo(
    () => ({
      loading: businessEntityUuid ? loading : false,
      orderTasksWithStatus: data?.orderTasksByBusinessEntity || [],
    }),
    [businessEntityUuid, loading, data],
  );

  return <OrderTrackerStatusContext.Provider value={value}>{children}</OrderTrackerStatusContext.Provider>;
}

function useOrderTrackerStatus() {
  const value = useContext(OrderTrackerStatusContext);
  if (!value) {
    throw new ZBError('useOrderTrackerStatus must be used within OrderTrackerStatusProvider');
  }
  return value;
}

export { useOrderTrackerStatus, OrderTrackerStatusProvider };
